import { Body, Button, Tooltip } from 'copilot-design-system';
import { useFormikContext } from 'formik';
import React from 'react';

import { useAppDispatch, useAppSelector } from 'src/hooks/useStore';
import DisableBillingTooltip from 'src/legacy/components/Payments/DisableAutoChargeTooltip';
import { FormHeaderWrapper } from 'src/legacy/components/UI/FormHeaderWrapper';
import { ShareProductStoreModal } from 'src/routes/billing/store/components/ShareProductStoreModal';
import {
  ProductStoreSchema,
  ProductStoreVisibility,
} from 'src/routes/billing/store/schema';
import { InvoiceIncompleteReasons } from 'src/services/api/invoicesApi';
import { useGetProductStoresQuery } from 'src/services/api/productStoreApi';
import { togglePrimarySidebarMobile } from 'src/store/ui/actions';

export interface FormProps {
  isSubmitting?: boolean;
  onSubmit: () => void;
  isNew: boolean;
  dirty?: boolean;
}

enum StoreStateLabel {
  UnsavedChanges = 'Unsaved changes',
  AllChangesSaved = 'All changes saved',
  SavedToWebsite = 'Published to Website',
  SavedToPortal = 'Published to Portal',
  SavedPrivate = 'Saved (Private)',
  SavedToPortalAndWebsite = 'Published to Portal & Website',
}

export interface Props extends FormProps {
  onClick: (data: ProductStoreSchema, isPublish: boolean) => Promise<void>;
  shareProductStoreModalOpen: boolean;
  onShareProductStoreModal: (open: boolean) => void;
  isProductStoreLoading: boolean;
  onCopyDomainUrl: () => void;
  basePortalDomain: string;
}

export const ProductStoreHeader = ({
  onClick,
  shareProductStoreModalOpen,
  onShareProductStoreModal,
  isProductStoreLoading,
  onCopyDomainUrl,
  basePortalDomain,
}: Props) => {
  const { values, dirty, errors } = useFormikContext<ProductStoreSchema>();

  const { data: productStores = [] } = useGetProductStoresQuery();
  const accountInfo = useAppSelector((state) => state.payments.accountInfo);
  const productStore = productStores.at(0);
  const dispatch = useAppDispatch();

  const { subscriptions, oneTimeProducts } = values;

  const doesEditingStateExist = React.useMemo(() => {
    const itemIsEditing = [...subscriptions, ...oneTimeProducts]
      .filter((item) => 'isEditing' in item)
      .some((item) => item.isEditing);

    return itemIsEditing;
  }, [subscriptions, oneTimeProducts]);

  const {
    publishDisabledTooltip: publishDisabledTooltip,
    savingDisabledTooltip,
    isPublishingDisabled,
    isSavingDisabled,
  } = React.useMemo(() => {
    const returnValue: {
      publishDisabledTooltip: React.ReactNode;
      savingDisabledTooltip: React.ReactNode;
      isPublishingDisabled: boolean;
      isSavingDisabled: boolean;
    } = {
      publishDisabledTooltip: '',
      savingDisabledTooltip: '',
      isPublishingDisabled: false,
      isSavingDisabled: false,
    };

    if (!accountInfo) {
      returnValue.publishDisabledTooltip = (
        <DisableBillingTooltip
          reason={InvoiceIncompleteReasons.NO_BANKING_INFO}
        />
      );
      returnValue.isPublishingDisabled = true;
      return returnValue;
    }
    if (!dirty) {
      returnValue.savingDisabledTooltip = (
        <Body size="sm" className="text-black-heading">
          You have no unsaved changes.
        </Body>
      );
      if (!productStore) {
        returnValue.publishDisabledTooltip = (
          <Body size="sm" className="text-black-heading">
            You have no unsaved changes to publish.
          </Body>
        );
        returnValue.isPublishingDisabled = true;
      }
      returnValue.isSavingDisabled = true;
      return returnValue;
    }
    if (doesEditingStateExist) {
      if (accountInfo) {
        returnValue.publishDisabledTooltip = (
          <Body size="sm" className="text-black-heading">
            Please finish editing your product before publishing.
          </Body>
        );
        returnValue.isPublishingDisabled = true;
      }
      returnValue.publishDisabledTooltip = (
        <Body size="sm" className="text-black-heading">
          Please finish editing your product before publishing.
        </Body>
      );
      returnValue.isPublishingDisabled = true;
      returnValue.savingDisabledTooltip = (
        <Body size="sm" className="text-black-heading">
          Please finish editing your product before saving.
        </Body>
      );
      returnValue.isSavingDisabled = true;
      return returnValue;
    }

    if (errors.displayName) {
      if (accountInfo) {
        returnValue.publishDisabledTooltip = (
          <Body size="sm" className="text-black-heading">
            {errors.displayName}
          </Body>
        );
        returnValue.isPublishingDisabled = true;
      }
      returnValue.publishDisabledTooltip = (
        <Body size="sm" className="text-black-heading">
          {errors.displayName}
        </Body>
      );
      returnValue.isPublishingDisabled = true;
      returnValue.savingDisabledTooltip = (
        <Body size="sm" className="text-black-heading">
          {errors.displayName}
        </Body>
      );
      returnValue.isSavingDisabled = true;
      return returnValue;
    }

    return returnValue;
  }, [accountInfo, doesEditingStateExist, dirty, errors.displayName]);

  const storeStateLabel = React.useMemo(() => {
    const visibility = productStore?.productStorePreferences?.visibility;

    if (dirty) {
      return StoreStateLabel.UnsavedChanges;
    }

    switch (visibility) {
      case ProductStoreVisibility.Public:
        return StoreStateLabel.SavedToWebsite;
      case ProductStoreVisibility.ClientUser:
        return StoreStateLabel.SavedToPortal;
      case ProductStoreVisibility.Internal:
        return StoreStateLabel.SavedPrivate;
      case ProductStoreVisibility.All:
        return StoreStateLabel.SavedToPortalAndWebsite;
      default:
        return '';
    }
  }, [dirty, productStore]);

  const handleSave = async () => {
    await onClick(values, false);
  };

  const handleShare = async () => {
    await onClick(values, true);
    onShareProductStoreModal(false);
  };

  const handleMenuButtonClick = () => {
    dispatch(togglePrimarySidebarMobile({ isOpen: true }));
  };

  return (
    <>
      <FormHeaderWrapper
        onMenuButtonClick={handleMenuButtonClick}
        showMenuButton
        breadcrumbItems={[
          {
            label: 'Store',
          },
        ]}
      >
        <div className="flex items-center gap-2.5">
          <Body size="xs" className="text-gray-small">
            {storeStateLabel}
          </Body>
          <Tooltip position="bottom" content={savingDisabledTooltip}>
            <span>
              <Button
                label="Save"
                variant="secondary"
                onClick={handleSave}
                disabled={isSavingDisabled}
              />
            </span>
          </Tooltip>
          <Tooltip position="bottom" content={publishDisabledTooltip}>
            <span>
              <Button
                type="submit"
                variant="primary"
                label="Publish"
                disabled={isPublishingDisabled}
              />
            </span>
          </Tooltip>
        </div>
      </FormHeaderWrapper>
      {shareProductStoreModalOpen && (
        <ShareProductStoreModal
          onCancel={() => onShareProductStoreModal(false)}
          handleShare={handleShare}
          open={shareProductStoreModalOpen}
          visibility={values.productStorePreferences.visibility}
          isProductStoreLoading={isProductStoreLoading}
          onCopyDomainUrl={onCopyDomainUrl}
          basePortalDomain={basePortalDomain}
        />
      )}
    </>
  );
};
